/**
 * Test Harness Component
 * Part of the FAANG-grade _app.tsx refactoring - Phase 5.1
 *
 * Conditionally loads all E2E testing utilities and network stubbing.
 * This component should only be rendered in testing environments.
 */

import { useRouter } from 'next/router';
import { useEffect } from 'react';

/**
 * Comprehensive test harness for E2E testing scenarios
 * Only renders when testing environment variables are set
 */
export const TestHarness: React.FC = () => {
  const router = useRouter();

  // Early bootstrap for Week 11 testing scenarios
  useEarlyTestBootstrap();

  // Quiet test logs to reduce noise
  useQuietTestLogs();

  // Week 11 navigation swallowing
  useNavigationSwallowing(router);

  // Analytics polling silencer for E2E tests
  useAnalyticsSilencer();

  // Dashboard network quieting for E2E tests
  useDashboardNetworkQuietening(router);

  // Render nothing - this component only provides side effects
  return null;
};

/**
 * Early bootstrap logic for Week 11 testing
 */
function useEarlyTestBootstrap() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const IS_W11 = process.env.SMOKE_HARNESS_WEEK === 'Week 11';
    const isW11Public = /^\/(pwa(\/|$)|crm\/pipeline$|services(\/|$)|quotes(\/|$))/.test(window.location.pathname);

    if (!IS_W11 && !isW11Public) return;

    try {
      const origFetch = window.fetch?.bind(window);
      if (origFetch && !(window as any).__w11EarlyStub) {
        (window as any).__w11EarlyStub = true;

        window.fetch = (input: any, init?: RequestInit) => {
          try {
            const urlStr = typeof input === 'string' ? input : (input as Request).url;
            const method = (init?.method || (typeof input !== 'string' ? (input as Request).method : 'GET') || 'GET').toUpperCase();

            if (IS_W11 && method === 'GET' && typeof urlStr === 'string') {
              const u = new URL(urlStr, window.location.origin);
              const sameOrigin = u.origin === window.location.origin;

              // Stub auth endpoints for W11
              if ((sameOrigin && /\/api\/auth\/me\b/.test(u.pathname)) || /\/auth\/whoami\b/.test(u.pathname)) {
                const stub = { user: { id: 'e2e', tenantId: 't1', email: 'e2e@stub', subdomain: 'e2e' } };
                return Promise.resolve(new Response(JSON.stringify(stub), {
                  status: 200, headers: { 'Content-Type': 'application/json' }
                }));
              }

              // Stub API responses when offline
              if (!navigator.onLine && sameOrigin && u.pathname.startsWith('/api/')) {
                let body: any = {};
                if (/\/customers\b/.test(u.pathname)) body = [];
                else if (/\/services\b/.test(u.pathname)) body = [];
                else if (/\/quotes\b/.test(u.pathname)) body = [];
                return Promise.resolve(new Response(JSON.stringify(body), {
                  status: 200, headers: { 'Content-Type': 'application/json' }
                }));
              }
            }
          } catch (error) {
            // Ignore URL parsing errors
          }
          return origFetch(input as any, init);
        };

        // Swallow login navigation in Week 11
        if (IS_W11) {
          const swallow = (u: any) => {
            try {
              const s = typeof u === 'string' ? u : (u && (u as any).href) || '';
              const path = (s.split('?')[0] || '');
              return path === '/login';
            } catch {
              return false;
            }
          };

          const pushState = history.pushState.bind(history);
          history.pushState = function (s, t, url) { if (!swallow(url)) return pushState(s, t, url); };

          const replaceState = history.replaceState.bind(history);
          history.replaceState = function (s, t, url) { if (!swallow(url)) return replaceState(s, t, url); };
        }
      }
    } catch (error) {
      console.warn('[TEST_HARNESS] Early bootstrap failed:', error);
    }
  }, []);
}

/**
 * Quiet console logs during testing to reduce noise
 */
function useQuietTestLogs() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (process.env.NEXT_PUBLIC_E2E_VERBOSE_LOGS === 'true') return;

    const orig = {
      log: window.console.log,
      info: window.console.info,
      warn: window.console.warn,
    };

    try {
      // No-op noisy channels; errors still surface
      window.console.log = () => {};
      window.console.info = () => {};
      window.console.warn = () => {};
    } catch (error) {
      console.warn('[TEST_HARNESS] Failed to quiet logs:', error);
    }

    return () => {
      try {
        window.console.log = orig.log;
        window.console.info = orig.info;
        window.console.warn = orig.warn;
      } catch (error) {
        console.warn('[TEST_HARNESS] Failed to restore logs:', error);
      }
    };
  }, []);
}

/**
 * Swallow navigation to /login during Week 11 testing
 */
function useNavigationSwallowing(router: ReturnType<typeof useRouter>) {
  useEffect(() => {
    const DISABLE_ANALYTICS = process.env.NEXT_PUBLIC_DEBUG_E2E === 'true' || process.env.NEXT_PUBLIC_DISABLE_ANALYTICS_POLLING === 'true';
    if (!DISABLE_ANALYTICS) return;

    const IS_W11 = process.env.SMOKE_HARNESS_WEEK === 'Week 11';
    if (!IS_W11) return;

    const w11Public = /^\/(pwa(\/|$)|crm\/pipeline$|services(\/|$)|quotes(\/|$))/.test(window.location.pathname);
    if (!w11Public) return;

    const swallow = (u: any) => {
      const s = typeof u === 'string' ? u : (u?.pathname || u?.href || '');
      let hit = false;
      try {
        const path = (s.split('?')[0] || '');
        hit = path === '/login';
      } catch {}
      if (hit) {
        try {
          console.info('[W11-TRACE] attempted nav->/login swallowed in test harness', {
            path: typeof window !== 'undefined' ? window.location.pathname : '',
          });
        } catch {}
      }
      return hit;
    };

    const origPush = (router as any).push?.bind(router);
    const origReplace = (router as any).replace?.bind(router);

    (router as any).push = (url: any, as?: any, opts?: any) =>
      swallow(url) ? Promise.resolve(true) : origPush(url, as, opts);
    (router as any).replace = (url: any, as?: any, opts?: any) =>
      swallow(url) ? Promise.resolve(true) : origReplace(url, as, opts);

    const onStart = (url: string) => { if (swallow(url)) { router.events.emit('routeChangeError'); throw 'cancelled'; } };
    router.events.on('routeChangeStart', onStart);

    return () => {
      router.events.off('routeChangeStart', onStart);
      if (origPush) (router as any).push = origPush;
      if (origReplace) (router as any).replace = origReplace;
    };
  }, [router]);
}

/**
 * Silence analytics polling during E2E tests
 */
function useAnalyticsSilencer() {
  useEffect(() => {
    const DISABLE_ANALYTICS = process.env.NEXT_PUBLIC_DEBUG_E2E === 'true' || process.env.NEXT_PUBLIC_DISABLE_ANALYTICS_POLLING === 'true';
    if (!DISABLE_ANALYTICS) return;
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

    const origFetch = window.fetch.bind(window);
    (window as any).__origFetch = origFetch;

    window.fetch = (input: any, init?: RequestInit) => {
      try {
        const url = typeof input === 'string' ? input : (input as Request).url;
        const method = (init?.method || (typeof input !== 'string' ? (input as Request).method : 'GET') || 'GET').toUpperCase();

        // Skip interception for static assets
        if (typeof url === 'string' && (
          url.includes('/_next/static/') ||
          url.includes('.css') || url.includes('.js') ||
          url.includes('.png') || url.includes('.jpg') || url.includes('.jpeg') ||
          url.includes('.gif') || url.includes('.svg') || url.includes('.ico') ||
          url.includes('.woff') || url.includes('.woff2') || url.includes('.ttf') || url.includes('.eot')
        )) {
          return origFetch(input as any, init);
        }

        if (method === 'GET' && typeof url === 'string' && /\/api\/analytics\/summary\b/.test(url)) {
          return Promise.resolve(
            new Response(JSON.stringify({ ok: true, tickets: 0, services: 0 }), {
              status: 200, headers: { 'Content-Type': 'application/json' }
            })
          );
        }
      } catch (error) {
        console.warn('[TEST_HARNESS] Analytics silencing failed:', error);
      }
      return origFetch(input as any, init);
    };

    return () => { window.fetch = origFetch; };
  }, []);
}

/**
 * Quiet network requests on dashboard during E2E tests
 */
function useDashboardNetworkQuietening(router: ReturnType<typeof useRouter>) {
  useEffect(() => {
    const E2E = process.env.NEXT_PUBLIC_DEBUG_E2E === 'true' || process.env.NEXT_PUBLIC_DISABLE_ANALYTICS_POLLING === 'true';
    if (!E2E) return;
    if (typeof window === 'undefined') return;

    const onDashboard = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/dashboard');
    if (!onDashboard()) return;

    // Fetch shim for dashboard
    const origFetch = window.fetch?.bind(window);
    if (origFetch) {
      (window as any).__quietFetch = origFetch;
      window.fetch = (input: any, init?: RequestInit) => {
        try {
          const url = typeof input === 'string' ? input : (input as Request).url;
          const method = (init?.method || (typeof input !== 'string' ? (input as Request).method : 'GET') || 'GET').toUpperCase();

          // Skip interception for static assets
          if (typeof url === 'string' && (
            url.includes('/_next/static/') ||
            url.includes('.css') || url.includes('.js') ||
            url.includes('.png') || url.includes('.jpg') || url.includes('.jpeg') ||
            url.includes('.gif') || url.includes('.svg') || url.includes('.ico') ||
            url.includes('.woff') || url.includes('.woff2') || url.includes('.ttf') || url.includes('.eot')
          )) {
            return origFetch(input as any, init);
          }

          if (method === 'GET' && typeof url === 'string') {
            if (/\/api\/auth\/me\b/.test(url)) {
              return Promise.resolve(
                new Response(JSON.stringify({ ok: true, user: { id: 'e2e', email: 'e2e@stub' } }), {
                  status: 200, headers: { 'Content-Type': 'application/json' }
                })
              );
            }
            if (/\/api\/services\b/.test(url)) {
              return Promise.resolve(
                new Response(JSON.stringify({ services: [] }), {
                  status: 200, headers: { 'Content-Type': 'application/json' }
                })
              );
            }
          }
        } catch (error) {
          console.warn('[TEST_HARNESS] Dashboard network quietening failed:', error);
        }
        return origFetch(input as any, init);
      };
    }

    // XHR shim for axios/browser libraries
    const OrigXHR = (window as any).XMLHttpRequest;
    function shouldStub(url: string, method: string) {
      if (method !== 'GET') return false;
      return /\/api\/auth\/me\b/.test(url) || /\/api\/services\b/.test(url);
    }

    class QuietXHR extends OrigXHR {
      private __url = '';
      private __method = 'GET';

      open(method: string, url: string, async?: boolean, user?: string, password?: string) {
        this.__method = (method || 'GET').toUpperCase();
        this.__url = url;
        return super.open(method, url, async as any, user as any, password as any);
      }

      send(body?: Document | BodyInit | null) {
        if (shouldStub(this.__url, this.__method)) {
          const payload = /auth\/me/.test(this.__url)
            ? { ok: true, user: { id: 'e2e', email: 'e2e@stub' } }
            : { services: [] };
          const json = JSON.stringify(payload);
          (this as any).readyState = 4;
          (this as any).status = 200;
          (this as any).responseText = json;
          (this as any).response = json;
          setTimeout(() => {
            this.onreadystatechange?.(new Event('readystatechange') as any);
            this.onload?.(new Event('load') as any);
          }, 0);
          return;
        }
        return super.send(body as any);
      }
    }

    (window as any).__OrigXHR = OrigXHR;
    (window as any).XMLHttpRequest = QuietXHR as any;

    // Cleanup when leaving dashboard
    return () => {
      if ((window as any).__quietFetch) window.fetch = (window as any).__quietFetch;
      if ((window as any).__OrigXHR) window.XMLHttpRequest = (window as any).__OrigXHR;
    };
  }, [router.pathname]);
}